import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { AlertCircle, Package } from "lucide-react";
import { api } from "../lib/api";
import type {
  ChannelDetail,
  ProductFacets,
  ProductListItem,
  ProductListResponse,
  ProductsOrder,
  PublicationStatus,
} from "../lib/types";
import { PageHeader } from "../components/layout/PageHeader";
import { Skeleton } from "../components/ui/skeleton";
import { EmptyState } from "../components/ui/empty-state";
import { Badge, type BadgeVariant } from "../components/ui/badge";
import { ArchivedBadge } from "../components/ui/ArchivedBadge";
import { formatDate, relativeTime } from "../lib/utils";
import { FilterPanel, type FilterGroup } from "../components/ui/FilterPanel";
import { Thumb } from "../components/ui/Thumb";
import { ChannelProductsCard, type ChannelTableColumn } from "../components/channel/ChannelProductsCard";
import { MlPendenciesPanel } from "../components/channel/MlPendenciesPanel";
import { MlRulesTab } from "../components/channel/MlRulesTab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../contexts/AuthContext";

import { pubStatusLabel } from "../lib/status-labels";

const STATUS_VARIANT: Record<PublicationStatus, BadgeVariant> = {
  live: "live",
  draft: "draft",
  unpublished: "unpublished",
  error: "error",
};

interface Filters {
  status: "" | PublicationStatus;
  hasPhoto: "" | "true" | "false";
  pending: "" | "true" | "false";
  /** Situacao do produto: "" e "false" = em operacao (padrao da API), "true" = so arquivados. */
  archived: "" | "true" | "false";
  order: ProductsOrder;
}

const DEFAULT_FILTERS: Filters = { status: "", hasPhoto: "", pending: "", archived: "", order: "recent" };

/** "Arquivado por <nome> em <data>" (so com o filtro Arquivados, quando a API manda quem/quando). */
function archivedByLine(item: ProductListItem): string | null {
  if (!item.archived_at) return null;
  return `Arquivado${item.archived_by_name ? ` por ${item.archived_by_name}` : ""} em ${formatDate(item.archived_at)}`;
}

export function ChannelPage(): JSX.Element {
  const { slug = "" } = useParams<{ slug: string }>();
  const { can } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);

  // Busca com debounce leve (~300ms): o campo continua controlado por `q`
  // (feedback instantâneo em cada tecla), mas quem alimenta as queries é
  // `debouncedQ` — evita um fetch por letra digitada. `q` sem debounce
  // continua existindo pra outros usos (ex: chip "Buscar SKU..." teria
  // usado o valor cru se precisasse).
  const [debouncedQ, setDebouncedQ] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Paginacao por cursor (API so devolve next_cursor, nunca total/pagina —
  // "nunca inventam total", mesma regra dos facets abaixo). pageCursors[i] e
  // o cursor usado pra buscar a pagina i (index 0 = null, primeira pagina).
  // Anterior so anda o ponteiro pra tras (sem novo fetch: a pagina ja foi
  // buscada); Proxima busca so quando avanca alem do que ja foi visitado.
  const [pageCursors, setPageCursors] = useState<(string | null)[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const cursor = pageCursors[pageIndex];

  const resetPagination = (): void => {
    setPageCursors([null]);
    setPageIndex(0);
  };

  const filtersKey = useMemo(
    () => ({ slug, q: debouncedQ, ...filters, cursor }),
    [slug, debouncedQ, filters, cursor],
  );

  const { data: channel } = useQuery<ChannelDetail>({
    queryKey: ["channel", slug],
    queryFn: async () => {
      const r = await api.get<ChannelDetail>(`/api/channels/${slug}`);
      return r.data;
    },
    enabled: slug.length > 0,
  });

  // Contagens reais dos filtros (W0 §4.2). Ausente em API antiga -> undefined,
  // e os grupos do FilterPanel caem pra "sem numero" (nunca quebram).
  const { data: facets } = useQuery<ProductFacets>({
    queryKey: ["channel-facets", slug, debouncedQ, filters.archived],
    queryFn: async () => {
      const r = await api.get<ProductFacets>(`/api/channels/${slug}/products/facets`, {
        params: { q: debouncedQ || undefined, archived: filters.archived || undefined },
      });
      return r.data;
    },
    enabled: slug.length > 0,
    retry: false,
    // Mantém as contagens antigas visíveis enquanto a nova chegada não
    // volta — sem isso os números do FilterPanel piscavam pra "sem
    // número" a cada letra digitada.
    placeholderData: keepPreviousData,
  });

  const { data: list, isLoading, isError, error } = useQuery<ProductListResponse>({
    queryKey: ["channel-products", filtersKey],
    queryFn: async () => {
      const r = await api.get<ProductListResponse>(`/api/channels/${slug}/products`, {
        params: {
          q: debouncedQ || undefined,
          status: filters.status || undefined,
          has_photo: filters.hasPhoto || undefined,
          pending: filters.pending || undefined,
          archived: filters.archived || undefined,
          order: filters.order !== "recent" ? filters.order : undefined,
          limit: 50,
          cursor: cursor || undefined,
        },
      });
      return r.data;
    },
    enabled: slug.length > 0,
    // BUG (relatado em prod, W-fix/campo-perde-foco): sem isto, cada
    // tecla digitada muda `filtersKey` -> `isLoading` volta a `true` ->
    // o ramo abaixo troca <ChannelProductsCard/> (que contém o <input>
    // de busca) por <Skeleton/>, desmontando o campo e derrubando o
    // foco. keepPreviousData faz a query ficar "success" com o dado
    // antigo enquanto busca o novo — isLoading só é true no load inicial.
    placeholderData: keepPreviousData,
  });

  const items = list?.items ?? [];

  const filterGroups: FilterGroup[] = [
    {
      key: "status",
      label: "Status",
      options: [
        { value: "live", label: "Publicado", count: facets?.status.live },
        { value: "draft", label: "Rascunho", count: facets?.status.draft },
        { value: "unpublished", label: "Despublicado", count: facets?.status.unpublished },
        { value: "error", label: "Erro", count: facets?.status.error },
      ],
    },
    {
      key: "hasPhoto",
      label: "Foto",
      options: [
        { value: "true", label: "Com foto", count: facets?.photo.with },
        { value: "false", label: "Sem foto", count: facets?.photo.without },
      ],
    },
    {
      key: "pending",
      label: "Pendências",
      options: [
        { value: "true", label: "Pendente", count: facets?.pending.yes },
        { value: "false", label: "Em dia", count: facets?.pending.no },
      ],
    },
    {
      key: "archived",
      label: "Situação",
      options: [
        { value: "false", label: "Em operação", count: facets?.archived?.no },
        { value: "true", label: "Arquivados", count: facets?.archived?.yes },
      ],
    },
    {
      key: "order",
      label: "Ordenação",
      options: [
        { value: "recent", label: "Mais recentes" },
        { value: "oldest", label: "Mais antigos" },
      ],
    },
  ];

  const filterValue: Record<string, string | null | undefined> = {
    status: filters.status || null,
    hasPhoto: filters.hasPhoto || null,
    pending: filters.pending || null,
    archived: filters.archived || null,
    order: filters.order !== "recent" ? filters.order : null,
  };

  const onFilterChange = (key: string, value: string | null): void => {
    resetPagination();
    setFilters((f) => {
      if (key === "order") return { ...f, order: (value as ProductsOrder) ?? "recent" };
      return { ...f, [key]: value ?? "" } as Filters;
    });
  };

  const onResetFilters = (): void => {
    setQ("");
    setFilters(DEFAULT_FILTERS);
    resetPagination();
  };

  // facets.total so cobre o filtro `q` e a situacao (archived vai nos params
  // dos facets, entao o total ja vale para o universo escolhido) — com
  // status/foto/pendencia tambem ativos ele deixa de bater com a lista
  // filtrada. Regra da casa e "nunca inventar total" (mesmo comentario);
  // entao o rodape so mostra "de N paginas — M no total" quando NENHUM
  // desses 3 filtros extra esta ativo. Fora disso, mostra so a pagina atual.
  const totalIsReliable = !filters.status && !filters.hasPhoto && !filters.pending;
  const totalPages =
    totalIsReliable && typeof facets?.total === "number" ? Math.max(1, Math.ceil(facets.total / 50)) : null;
  const hasPrevPage = pageIndex > 0;
  const hasNextPage = Boolean(list?.next_cursor);

  const goPrevPage = (): void => setPageIndex((i) => Math.max(0, i - 1));
  const goNextPage = (): void => {
    const next = list?.next_cursor;
    if (!next) return;
    setPageCursors((prev) => (pageIndex + 1 < prev.length ? prev : [...prev, next]));
    setPageIndex((i) => i + 1);
  };

  const hasActiveFilters =
    Boolean(q) || filters.status || filters.hasPhoto || filters.pending || filters.archived === "true" || filters.order !== "recent";

  const columns: ChannelTableColumn<ProductListItem>[] = [
    {
      key: "cover",
      label: "Capa",
      width: "2.5rem",
      render: (item) => <Thumb src={item.cover_url} alt="" size="2.5rem" />,
    },
    {
      key: "sku",
      label: "SKU / Nome",
      width: "minmax(0,4fr)",
      render: (item) => (
        <Link to={`/canais/${slug}/produtos/${item.sku}`} style={{ textDecoration: "none", color: "inherit" }}>
          <div className="mono muted" style={{ fontSize: "var(--fs-micro)" }}>
            {item.sku}
          </div>
          <div className="truncate" style={{ fontWeight: "var(--fw-black)" }}>
            {item.name || "(sem nome)"}
          </div>
          {archivedByLine(item) ? (
            <div className="muted truncate" style={{ fontSize: "var(--fs-micro)" }}>
              {archivedByLine(item)}
            </div>
          ) : null}
        </Link>
      ),
    },
    {
      key: "status",
      label: "Status",
      width: "minmax(5rem,2fr)",
      render: (item) => (
        <div className="cluster" style={{ gap: ".25rem" }}>
          <Badge variant={STATUS_VARIANT[item.status]}>{pubStatusLabel(item.status)}</Badge>
          {item.archived ? <ArchivedBadge title={archivedByLine(item) ?? undefined} /> : null}
        </div>
      ),
    },
    {
      key: "pend",
      label: "Pend.",
      width: "minmax(4.5rem,2fr)",
      render: (item) =>
        item.pending_changes ? (
          <Badge variant="warning" title="Há alterações ainda não publicadas na loja">
            Pendente
          </Badge>
        ) : (
          <span className="muted">—</span>
        ),
    },
    {
      key: "sync",
      label: "Sync",
      width: "minmax(5rem,2fr)",
      align: "right",
      render: (item) => <span className="muted text-micro">{relativeTime(item.last_synced_at)}</span>,
    },
  ];

  // Total do canal pro subtítulo (brief: "Canal atacado · adapter wc · 198
  // produtos"). facets.total cobre o filtro `q` (comentário acima, W0 §4.2)
  // — sem `q` ele é o total do canal inteiro, que é exatamente o que o
  // subtítulo precisa mostrar. Ausente em API antiga -> some do subtítulo
  // (nunca inventa número, mesma regra do rodapé de paginação).
  const channelTotal = typeof facets?.total === "number" ? facets.total : null;
  const onlyArchived = filters.archived === "true";

  const showRulesTab = channel?.adapter === "ml" && can("channel_rules.write");
  const tab = showRulesTab && searchParams.get("aba") === "regras" ? "regras" : "produtos";

  return (
    <div>
      <PageHeader
        title={channel?.name ?? slug}
        description={
          channel
            ? `Canal ${channel.kind} · adapter ${channel.adapter}${channelTotal !== null ? ` · ${channelTotal} ${onlyArchived ? (channelTotal === 1 ? "arquivado" : "arquivados") : "produtos"}` : ""}`
            : `Canal ${slug}`
        }
        breadcrumb={
          <Link to="/" className="hover:underline">
            Home
          </Link>
        }
      />

      {showRulesTab ? (
        <Tabs value={tab} onValueChange={(v) => setSearchParams(v === "regras" ? { aba: "regras" } : {}, { replace: true })}>
          <TabsList>
            <TabsTrigger value="produtos">Produtos</TabsTrigger>
            <TabsTrigger value="regras">Regras do Mercado Livre</TabsTrigger>
          </TabsList>
          <TabsContent value="regras">
            <MlRulesTab slug={slug} />
          </TabsContent>
        </Tabs>
      ) : null}
      <div className="flex flex-col lg:flex-row lg:items-start gap-4" style={showRulesTab && tab === "regras" ? { display: "none" } : undefined}>
        {/* FilterPanel devolve DOIS elementos (aside desktop + gatilho mobile)
            via Fragment — nunca usar `.with-filters` (sempre row) aqui: no
            mobile o gatilho vira um segundo item de flex-row e espreme o
            corpo (bug medido: body ficava com metade da largura). Column no
            mobile, row so a partir de lg. */}
        <FilterPanel groups={filterGroups} value={filterValue} onChange={onFilterChange} onClear={onResetFilters} />

        <div className="flex-1 min-w-0 flex flex-col gap-4">
          {channel?.adapter === "ml" ? <MlPendenciesPanel slug={slug} /> : null}
          {isLoading ? (
            <div className="stack">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : isError ? (
            <EmptyState
              icon={<AlertCircle className="h-12 w-12" />}
              title="Erro ao listar produtos"
              description={(error as Error)?.message ?? "Tente recarregar"}
            />
          ) : (
            // Cartão único (brief fidelidade-3: capa ~2.5rem, tabela+busca+
            // paginação no MESMO cartão, sem cartão separado de busca nem
            // rodapé como irmão embaixo).
            <ChannelProductsCard
              columns={columns}
              rows={items}
              rowKey={(item) => item.sku}
              renderCard={(item) => <ProductCardMobile slug={slug} item={item} />}
              isEmpty={items.length === 0}
              emptyState={
                <EmptyState
                  icon={<Package className="h-12 w-12" />}
                  title="Nenhum produto encontrado"
                  description={
                    onlyArchived && !q
                      ? "Nenhum produto arquivado neste canal."
                      : hasActiveFilters
                        ? "Ajuste os filtros ou limpe-os para ver todos os produtos."
                        : "Nenhum produto cadastrado neste canal."
                  }
                />
              }
              search={{
                value: q,
                onChange: (value) => {
                  setQ(value);
                  resetPagination();
                },
                placeholder: "Buscar SKU, nome...",
                ariaLabel: "Buscar produtos no canal",
              }}
              pagination={{
                label: `Página ${pageIndex + 1}${totalPages ? ` de ${totalPages} — ${facets?.total} no total` : ""}`,
                hasPrev: hasPrevPage,
                hasNext: hasNextPage,
                onPrev: goPrevPage,
                onNext: goNextPage,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function ProductCardMobile({ slug, item }: { slug: string; item: ProductListItem }): JSX.Element {
  return (
    <Link to={`/canais/${slug}/produtos/${item.sku}`} style={{ textDecoration: "none", color: "inherit" }}>
      <div className="row-card">
        <Thumb src={item.cover_url} alt="" />
        <div className="row-card__body">
          <div className="cluster">
            <span className="mono muted" style={{ fontSize: "var(--fs-micro)" }}>
              {item.sku}
            </span>
            <Badge variant={STATUS_VARIANT[item.status]}>{pubStatusLabel(item.status)}</Badge>
            {item.archived ? <ArchivedBadge title={archivedByLine(item) ?? undefined} /> : null}
            {item.pending_changes ? (
              <Badge variant="warning" title="Há alterações ainda não publicadas na loja">
                Pendente
              </Badge>
            ) : null}
          </div>
          <div className="truncate" style={{ fontWeight: "var(--fw-black)" }}>
            {item.name || "(sem nome)"}
          </div>
          <div className="muted" style={{ fontSize: "var(--fs-micro)" }}>
            {archivedByLine(item) ?? relativeTime(item.last_synced_at)}
          </div>
        </div>
      </div>
    </Link>
  );
}
